import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaRocket, FaArrowRight, FaTerminal } from 'react-icons/fa';
import {
  HeroWrapper,
  HeroShell,
  GridLines,
  FloatingOrb,
  HeroContent,
  Badge,
  HeroTitle,
  HeroSubtitle,
  HeroButtons,
  BtnPrimary,
  BtnOutline,
  HeroTags,
  HeroTag,
  HeroNote,
  StatsRow,
  Stat,
  HeroPanel,
  PanelIntro,
  ProcessList,
  ProcessCard,
  ProcessIndex,
  ProcessCopy,
  PanelMetrics,
  MetricCard,
  CodeCard,
} from './Hero.styles';

const Hero: React.FC = () => {
  const { t } = useTranslation();

  const stats = [
    { value: '20+', label: t('hero.stat0Label') },
    { value: '60+', label: t('hero.stat1Label') },
    { value: '40+', label: t('hero.stat2Label') },
  ];

  const heroTags = ['React', 'PWA', 'Expo', 'TypeScript'];

  const heroSteps = [
    { index: '01', title: t('hero.step0Title'), desc: t('hero.step0Desc') },
    { index: '02', title: t('hero.step1Title'), desc: t('hero.step1Desc') },
    { index: '03', title: t('hero.step2Title'), desc: t('hero.step2Desc') },
  ];

  const heroMetrics = [
    { label: t('hero.metric0Label'), value: t('hero.metric0Value') },
    { label: t('hero.metric1Label'), value: t('hero.metric1Value') },
    { label: t('hero.metric2Label'), value: t('hero.metric2Value') },
  ];

  return (
    <HeroWrapper>
      <GridLines />
      <FloatingOrb $size={400} $top="-10%" $left="-10%" />
      <FloatingOrb $size={300} $top="50%" $left="70%" $delay="2s" />
      <FloatingOrb $size={200} $top="70%" $left="10%" $delay="1s" />

      <HeroShell>
        <HeroContent>
          <Badge>
            <FaTerminal size={12} />
            {t('hero.badge')}
          </Badge>

          <HeroTitle>
            {t('hero.title1')}{' '}
            <br className="hero-break" />
            <span className="gradient">{t('hero.titleGradient')}</span>
            <span className="title-tail"> {t('hero.title2')}</span>
          </HeroTitle>

          <HeroSubtitle>{t('hero.subtitle')}</HeroSubtitle>

          <HeroButtons>
            <BtnPrimary to="/uslugi">
              <FaRocket size={14} />
              {t('hero.ctaPrimary')}
            </BtnPrimary>
            <BtnOutline to="/kontakt">
              {t('hero.ctaSecondary')} <FaArrowRight size={13} />
            </BtnOutline>
          </HeroButtons>

          <HeroTags>
            {heroTags.map((tag) => (
              <HeroTag key={tag}>{tag}</HeroTag>
            ))}
          </HeroTags>

          <HeroNote>{t('hero.platformLabel')}</HeroNote>

          <StatsRow>
            {stats.map((s) => (
              <Stat key={s.label}>
                <div className="value">{s.value}</div>
                <div className="label">{s.label}</div>
              </Stat>
            ))}
          </StatsRow>
        </HeroContent>

        <HeroPanel>
          <PanelIntro>
            <span>{t('hero.planLabel')}</span>
            <h2>{t('hero.planTitle')}</h2>
            <p>{t('hero.planSubtitle')}</p>
          </PanelIntro>

          <ProcessList>
            {heroSteps.map((step) => (
              <ProcessCard key={step.index}>
                <ProcessIndex>{step.index}</ProcessIndex>
                <ProcessCopy>
                  <strong>{step.title}</strong>
                  <p>{step.desc}</p>
                </ProcessCopy>
              </ProcessCard>
            ))}
          </ProcessList>

          <PanelMetrics>
            {heroMetrics.map((metric) => (
              <MetricCard key={metric.label}>
                <span>{metric.label}</span>
                <strong>{metric.value}</strong>
              </MetricCard>
            ))}
          </PanelMetrics>

          <CodeCard>
            <div><span className="keyword">const</span> app = <span className="func">createApp</span>&#40;&#123;</div>
            <div>&nbsp;&nbsp;<span className="string">"platform"</span>: <span className="string">"web + mobile"</span>,</div>
            <div>&nbsp;&nbsp;<span className="string">"stack"</span>: <span className="string">"React + TS"</span>,</div>
            <div>&nbsp;&nbsp;<span className="string">"pwa"</span>: <span className="keyword">true</span>,</div>
            <div>&#125;&#41;<span className="cursor" /></div>
          </CodeCard>
        </HeroPanel>
      </HeroShell>
    </HeroWrapper>
  );
};

export default Hero;
